import { type FC, type PropsWithChildren } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormControl, FormHelperText, Grid } from '@mui/material';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';
import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import { FileInput } from '#apps/client-web/features/file-storage/components/file-input/file-input';

import {
  SubFormDialog,
  type SubFormDialogProps,
} from '#packages/web/layouts/dialog/templates/sub-form';
import { DeleteConfirmationDialog } from '#packages/web/layouts/dialog/templates/delete-confirmation';
import { useDialog } from '#packages/web/hooks/use-dialog';

export type CoverPhotoFormDialogProps = Pick<
  SubFormDialogProps,
  'onClose' | 'open'
> & {
  control: Control<EditAuditFormBackgroundData>;
};

export const CoverPhotoFormDialog: FC<
  PropsWithChildren<CoverPhotoFormDialogProps>
> = ({ open, control, onClose }) => {
  const deletePhotoDialog = useDialog<{ proceed: () => void }>();

  return (
    <SubFormDialog title="Cover photo" open={open} onClose={onClose}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Controller
            control={control}
            name="envelope.coverPhoto"
            render={({ field, fieldState }) => (
              <FormControl
                fullWidth
                {...getFormErrorAttributes(fieldState.error)}>
                <FileInput
                  {...field}
                  onChange={(files) =>
                    field.onChange(
                      files.map((f) => ({ ...f, isSelectedForReport: true })),
                    )
                  }
                  label="Cover photo"
                  accept="image/*"
                  multiple={false}
                  hideSelectedForReport
                  onRemoveRequest={(_, proceed) =>
                    deletePhotoDialog.open({ proceed })
                  }
                  {...getFormErrorAttributes(fieldState.error)}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
      </Grid>

      <DeleteConfirmationDialog
        open={deletePhotoDialog.isOpen}
        dialogTitle="Remove Cover Photo"
        confirmButtonLabel="Remove"
        onConfirm={() => {
          deletePhotoDialog.data?.proceed();
          deletePhotoDialog.close();
        }}
        onCancel={() => deletePhotoDialog.close()}
      />
    </SubFormDialog>
  );
};
